// The garage: a stylized car that reacts to every questionnaire answer.
// Pure SVG (fast on a phone at the roadside) — the insurer becomes the garage
// banner, the ZIP a rally-style door number, impact points clickable zones,
// symptoms visual FX, and the rules dataset drives live "damage profile" bars.
// Presentation only: every control mirrors the real form state.
import type { ImpactPoint } from "../shared/questionnaire";
import type { DamagePreview } from "./rulesPreview";

export const CAR_COLORS = [
  "#f97316", // orange
  "#c9ced6", // silver
  "#c62828", // red
  "#1e88e5", // blue
  "#22262b", // black
  "#f9a825", // yellow
  "#2e7d32", // green
];

export interface CarState {
  color: string;
  insurerName?: string;
  zip?: string;
  collisionType: string;
  impactPoints: string[];
  symptoms: string[];
}

/**
 * Banner text with word-boundary truncation (shared by the SVG and 3D scenes).
 * Empty when there is no insurer — callers hide the banner entirely.
 */
export function bannerLabel(insurerName?: string): string {
  if (!insurerName) return "";
  const raw = insurerName.toUpperCase();
  return raw.length > 28
    ? `${raw.slice(0, raw.lastIndexOf(" ", 26) > 8 ? raw.lastIndexOf(" ", 26) : 26)}…`
    : raw;
}

const has = (arr: string[], v: string) => arr.includes(v);

// Multiply a hex color's channels — cheap shading for gradients/panels.
function shade(hex: string, f: number): string {
  const n = parseInt(hex.slice(1), 16);
  const c = (v: number) => Math.max(0, Math.min(255, Math.round(v * f)));
  const r = c((n >> 16) & 255);
  const g = c((n >> 8) & 255);
  const b = c(n & 255);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

function Wheel({ cx }: { cx: number }) {
  return (
    <g transform={`translate(${cx}, 128)`}>
      <circle r="41" fill="#0b0f14" />
      <circle r="38" fill="url(#tireGrad)" stroke="#05070a" strokeWidth="2.5" />
      <circle r="21" fill="url(#rimGrad)" stroke="#3a444f" strokeWidth="1.5" />
      {[0, 72, 144, 216, 288].map((a) => (
        <path
          key={a}
          d="M0,-17 L4.5,-5 L-4.5,-5 Z"
          fill="#5c6873"
          transform={`rotate(${a})`}
        />
      ))}
      <circle r="5.5" fill="#2a323c" stroke="#0b0f14" strokeWidth="1.5" />
    </g>
  );
}

/** Side-view car in a garage. Decorative twin of the form state. */
export function GarageCar({ car }: { car: CarState }) {
  const { color, insurerName, zip, collisionType, impactPoints, symptoms } = car;
  const front =
    has(impactPoints, "front") ||
    collisionType === "front-impact" ||
    collisionType === "multi-point";
  const rear =
    has(impactPoints, "rear") ||
    collisionType === "rear-impact" ||
    collisionType === "multi-point";
  const side =
    has(impactPoints, "driver-side") ||
    has(impactPoints, "passenger-side") ||
    collisionType === "side-impact-driver" ||
    collisionType === "side-impact-passenger" ||
    collisionType === "sideswipe";
  const roof = has(impactPoints, "roof") || collisionType === "rollover";
  const under = has(impactPoints, "undercarriage");
  const glassCracked = roof || collisionType === "rollover";
  const leak = has(symptoms, "leaking-fluid");
  const smoke = has(symptoms, "smoke-or-burning");
  const warning = has(symptoms, "warning-lights") || has(symptoms, "wont-start");
  const airbag = has(symptoms, "airbag-deployed");

  // Word-boundary truncation + size step-down keeps long names inside the banner.
  const banner = bannerLabel(insurerName);

  const bodyHi = shade(color, 1.45);
  const bodyLo = shade(color, 0.55);

  return (
    <svg
      className="garage-svg"
      viewBox="0 0 640 300"
      role="img"
      aria-label={`Your car in the garage${insurerName ? `, insured by ${insurerName}` : ""}`}
    >
      <defs>
        <linearGradient id="wallG" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1a2531" />
          <stop offset="100%" stopColor="#101821" />
        </linearGradient>
        <linearGradient id="floorG" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#151d27" />
          <stop offset="100%" stopColor="#0a0f15" />
        </linearGradient>
        <linearGradient id="coneG" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffe9a8" stopOpacity="0.16" />
          <stop offset="100%" stopColor="#ffe9a8" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="bodyG" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={bodyHi} />
          <stop offset="42%" stopColor={color} />
          <stop offset="100%" stopColor={bodyLo} />
        </linearGradient>
        <linearGradient id="glassG" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#68829d" />
          <stop offset="100%" stopColor="#1b2634" />
        </linearGradient>
        <radialGradient id="tireGrad">
          <stop offset="60%" stopColor="#1d242c" />
          <stop offset="100%" stopColor="#0b0f14" />
        </radialGradient>
        <radialGradient id="rimGrad">
          <stop offset="0%" stopColor="#e8edf2" />
          <stop offset="100%" stopColor="#8a95a1" />
        </radialGradient>
        <linearGradient id="headG" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#fff8d8" />
          <stop offset="100%" stopColor="#ffd873" />
        </linearGradient>
        <linearGradient id="tailG" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#ff8f8f" />
          <stop offset="100%" stopColor="#b71f1f" />
        </linearGradient>
      </defs>

      {/* garage backdrop */}
      <rect x="0" y="0" width="640" height="232" fill="url(#wallG)" />
      <line x1="0" y1="120" x2="640" y2="120" stroke="#0d141c" strokeWidth="1.5" opacity="0.5" />
      <rect x="0" y="232" width="640" height="68" fill="url(#floorG)" />
      <line x1="0" y1="232" x2="640" y2="232" stroke="#2c3d52" strokeWidth="2" />
      <line x1="0" y1="262" x2="640" y2="262" stroke="#0d141c" strokeWidth="1.5" opacity="0.6" />

      {/* overhead lamp + light cone */}
      <rect x="240" y="10" width="160" height="8" rx="4" fill="#202b38" stroke="#0b1118" />
      <rect x="246" y="17" width="148" height="3" rx="1.5" fill="#ffe9a8" opacity="0.85" />
      <polygon points="246,20 394,20 500,232 140,232" fill="url(#coneG)" />

      {/* insurer banner */}
      {banner && (
        <>
          <rect x="168" y="28" width="304" height="38" rx="8" fill="#0d1622" stroke="#33547c" strokeWidth="2" />
          <rect x="174" y="60" width="292" height="2.5" rx="1" fill="#33547c" opacity="0.5" />
          <text
            x="320"
            y="53"
            textAnchor="middle"
            fill="#60a5fa"
            fontSize={banner.length > 22 ? 12 : 17}
            fontWeight="700"
            letterSpacing={banner.length > 22 ? 0.8 : 2}
          >
            {banner}
          </text>
        </>
      )}

      {/* toolbox (left) */}
      <g transform="translate(28, 138)">
        <rect x="0" y="0" width="64" height="94" rx="5" fill="#a03030" stroke="#05070a" strokeWidth="2" />
        <rect x="0" y="0" width="64" height="14" rx="5" fill="#7c2222" />
        {[22, 46, 70].map((y) => (
          <g key={y}>
            <line x1="4" y1={y} x2="60" y2={y} stroke="#05070a" strokeWidth="1.5" opacity="0.6" />
            <rect x="22" y={y + 6} width="20" height="4" rx="2" fill="#d8dde3" opacity="0.8" />
          </g>
        ))}
      </g>
      {/* tire stack (right) */}
      <g stroke="#05070a" strokeWidth="2">
        <circle cx="592" cy="214" r="21" fill="#141a21" />
        <circle cx="592" cy="214" r="8" fill="#2a323c" />
        <circle cx="592" cy="188" r="21" fill="#181f27" />
        <circle cx="592" cy="188" r="8" fill="#2a323c" />
      </g>

      {/* car shadow */}
      <ellipse cx="322" cy="268" rx="242" ry="12" fill="#000" opacity="0.4" />
      {leak && <ellipse cx="470" cy="272" rx="46" ry="7" fill="#365a40" opacity="0.85" />}

      {/* car */}
      <g transform="translate(92, 96)">
        {/* body */}
        <path
          d={
            "M16,126 C6,126 1,117 1,106 L3,92 C5,81 13,74 27,70 L78,60 " +
            "C88,40 104,26 128,19 C158,10 196,7 232,7 C270,7 300,12 322,24 " +
            "C338,33 350,45 362,56 L410,62 C438,66 452,77 456,92 L457,104 " +
            "C458,117 451,126 440,126 L419,126 A43,43 0 0 0 333,126 " +
            "L155,126 A43,43 0 0 0 69,126 Z"
          }
          fill="url(#bodyG)"
          stroke="#0a0f14"
          strokeWidth="3"
        />
        {/* rocker */}
        <path d="M70,114 L420,114 L416,126 L74,126 Z" fill={bodyLo} opacity="0.9" />
        {/* greenhouse */}
        <path
          d="M96,62 C106,42 124,28 148,22 C186,13 230,12 266,16 C292,19 312,29 331,45 L343,60 L96,62 Z"
          fill="url(#glassG)"
          stroke="#0a0f14"
          strokeWidth="2"
        />
        <path d="M150,24 L192,18 L152,58 L130,56 Z" fill="#ffffff" opacity="0.16" />
        <polygon points="234,13 246,13 240,62 228,62" fill={color} stroke="#0a0f14" strokeWidth="1.5" />
        <line x1="96" y1="63" x2="343" y2="61" stroke="#d8dde3" strokeWidth="1.5" opacity="0.45" />
        {/* seams + handles */}
        <path d="M238,62 C240,84 240,104 238,124" stroke="#0a0f14" strokeWidth="2" fill="none" opacity="0.8" />
        <path d="M150,62 C150,84 150,104 152,124" stroke="#0a0f14" strokeWidth="2" fill="none" opacity="0.5" />
        <rect x="250" y="72" width="24" height="5" rx="2.5" fill={bodyLo} stroke="#0a0f14" strokeWidth="1" />
        <rect x="162" y="72" width="24" height="5" rx="2.5" fill={bodyLo} stroke="#0a0f14" strokeWidth="1" />
        {/* shoulder highlight */}
        <path
          d="M30,72 C140,58 330,58 446,84"
          stroke="#ffffff"
          strokeWidth="5"
          strokeLinecap="round"
          fill="none"
          opacity="0.12"
        />
        {/* hood seam */}
        <path d="M362,58 C380,56 396,58 410,62" stroke="#0a0f14" strokeWidth="1.5" fill="none" opacity="0.6" />
        {/* rally door badge = ZIP */}
        <circle cx="196" cy="92" r="29" fill="#f4f6f8" stroke="#10151c" strokeWidth="4" />
        <circle cx="196" cy="92" r="22" fill="none" stroke="#10151c" strokeWidth="1.5" opacity="0.3" />
        <text x="196" y="97" textAnchor="middle" fill="#141b24" fontSize={zip ? 14 : 16} fontWeight="800">
          {zip || "—"}
        </text>
        {/* lights + grille */}
        <polygon points="436,74 456,84 454,96 432,88" fill="url(#headG)" stroke="#0a0f14" strokeWidth="1.5" />
        <rect x="430" y="104" width="24" height="8" rx="3" fill="#0b0f14" opacity="0.85" />
        <path d="M2,80 L14,76 L14,94 L2,92 Z" fill="url(#tailG)" stroke="#0a0f14" strokeWidth="1.5" />
        {/* wheels */}
        <Wheel cx={112} />
        <Wheel cx={376} />

        {/* damage FX */}
        {front && (
          <g>
            <polygon
              points="448,66 458,74 452,84 458,96 448,104 442,88"
              fill={bodyLo}
              opacity="0.9"
              stroke="#0a0f14"
              strokeWidth="1.5"
            />
            <path d="M436,70 l14,12 m-18,-2 l14,12" stroke="#0a0f14" strokeWidth="2" fill="none" />
            <path d="M440,80 l10,6 m-8,-10 l6,12" stroke="#ffffff" strokeWidth="1.5" opacity="0.7" fill="none" />
          </g>
        )}
        {rear && (
          <g>
            <polygon
              points="2,78 12,72 10,86 16,94 6,100 2,92"
              fill={bodyLo}
              opacity="0.9"
              stroke="#0a0f14"
              strokeWidth="1.5"
            />
            <path d="M6,76 l10,10 m-12,4 l10,10" stroke="#0a0f14" strokeWidth="2" fill="none" />
          </g>
        )}
        {side && (
          <g>
            <ellipse cx="200" cy="102" rx="26" ry="12" fill={bodyLo} opacity="0.55" />
            <path
              d="M160,88 q20,10 44,4 M180,106 q22,8 46,2 M268,84 q14,8 30,3"
              stroke={shade(color, 0.35)}
              strokeWidth="2.5"
              fill="none"
            />
          </g>
        )}
        {roof && (
          <path d="M180,10 l12,7 l12,-7 M240,8 l10,6 l11,-6" stroke="#0a0f14" strokeWidth="2.5" fill="none" />
        )}
        {under && (
          <path d="M120,138 l18,5 m26,-3 l18,5 m36,-5 l18,5" stroke="#8a95a1" strokeWidth="2.5" fill="none" />
        )}
        {glassCracked && (
          <g stroke="#dfe7ee" strokeWidth="1.5" fill="none" opacity="0.85">
            <path d="M298,32 l16,9 M298,32 l-5,16 M298,32 l14,-7 M298,32 l-14,5" />
            <circle cx="298" cy="32" r="2.5" fill="#dfe7ee" stroke="none" />
          </g>
        )}
        {airbag && (
          <g>
            <ellipse cx="336" cy="44" rx="16" ry="12" fill="#f4f6f8" stroke="#0a0f14" strokeWidth="2" />
            <path d="M328,44 q8,-7 16,0 M330,49 q6,4 12,0" stroke="#c9ced6" strokeWidth="1.5" fill="none" />
          </g>
        )}
        {warning && (
          <g>
            <polygon points="311,40 320,54 302,54" fill="#f97316" stroke="#0a0f14" strokeWidth="1.5" />
            <text x="311" y="52" textAnchor="middle" fontSize="10" fontWeight="900" fill="#16212e">
              !
            </text>
          </g>
        )}
        {smoke && (
          <g fill="#9fb0c3" opacity="0.5">
            <circle cx="440" cy="30" r="9" />
            <circle cx="456" cy="14" r="7" />
            <circle cx="468" cy="2" r="5" />
          </g>
        )}
      </g>
    </svg>
  );
}

// Top-down damage diagram: the canonical way to point at impact zones, and it
// maps 1:1 onto the questionnaire's vocabulary. Buttons mirror it for a11y.
const ZONES: { id: ImpactPoint; label: string; d: string; lx: number; ly: number }[] = [
  { id: "front", label: "Front", d: "M150,10 h60 a14,14 0 0 1 14,14 v26 h-88 v-26 a14,14 0 0 1 14,-14 Z", lx: 180, ly: 38 },
  { id: "rear", label: "Rear", d: "M136,210 h88 v26 a14,14 0 0 1 -14,14 h-60 a14,14 0 0 1 -14,-14 Z", lx: 180, ly: 234 },
  { id: "driver-side", label: "Driver", d: "M104,54 h44 v152 h-44 Z", lx: 126, ly: 134 },
  { id: "passenger-side", label: "Passngr", d: "M212,54 h44 v152 h-44 Z", lx: 234, ly: 134 },
  { id: "roof", label: "Roof", d: "M152,84 h56 v92 h-56 Z", lx: 180, ly: 134 },
  // The one zone a top-down view can't show — rendered as a strip below the car.
  { id: "undercarriage", label: "Underneath", d: "M120,266 h120 a10,10 0 0 1 10,10 v4 a10,10 0 0 1 -10,10 h-120 a10,10 0 0 1 -10,-10 v-4 a10,10 0 0 1 10,-10 Z", lx: 180, ly: 284 },
];

export function ImpactDiagram({
  color,
  selected,
  onToggle,
}: {
  color: string;
  selected: string[];
  onToggle: (id: ImpactPoint) => void;
}) {
  return (
    <svg className="impact-svg" viewBox="90 0 180 296" aria-hidden="true">
      <defs>
        <linearGradient id="tdG" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={shade(color, 0.75)} />
          <stop offset="50%" stopColor={shade(color, 1.35)} />
          <stop offset="100%" stopColor={shade(color, 0.75)} />
        </linearGradient>
        <linearGradient id="tdGlass" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#5d768f" />
          <stop offset="100%" stopColor="#22303f" />
        </linearGradient>
      </defs>
      {/* car outline (top-down) */}
      <rect x="122" y="8" width="116" height="244" rx="34" fill="url(#tdG)" stroke="#0d131b" strokeWidth="3" />
      <line x1="180" y1="12" x2="180" y2="60" stroke="#0d131b" strokeWidth="1.5" opacity="0.35" />
      <path d="M138,64 q42,-16 84,0 l-6,26 q-36,-12 -72,0 Z" fill="url(#tdGlass)" stroke="#0d131b" strokeWidth="2" />
      <path d="M140,206 q40,14 80,0 l-4,-20 q-36,10 -72,0 Z" fill="url(#tdGlass)" stroke="#0d131b" strokeWidth="2" />
      <rect x="108" y="70" width="16" height="10" rx="4" fill={shade(color, 0.85)} stroke="#0d131b" strokeWidth="2" />
      <rect x="236" y="70" width="16" height="10" rx="4" fill={shade(color, 0.85)} stroke="#0d131b" strokeWidth="2" />
      {ZONES.map((z) => {
        const on = selected.includes(z.id);
        return (
          <g
            key={z.id}
            className={`impact-zone ${on ? "on" : ""}`}
            onClick={() => onToggle(z.id)}
            role="button"
            aria-label={`${z.label} impact`}
          >
            <path d={z.d} />
            <text x={z.lx} y={z.ly} textAnchor="middle">
              {on ? "✕" : z.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// Saints-Row-style stat readout, driven by the live rules preview.
export function StatBars({
  preview,
  title = "Damage profile",
  note,
}: {
  preview: DamagePreview;
  title?: string;
  note?: string;
}) {
  // ~16 is the most parts a realistic single-collision selection produces in
  // the dataset; the bar saturates beyond that while the text keeps counting.
  const bars = [
    { label: "Parts likely affected", value: Math.min(1, preview.partCount / 16), text: `${preview.partCount} part${preview.partCount === 1 ? "" : "s"}` },
    { label: "Est. severity", value: preview.maxSeverity / 10, text: preview.maxSeverity ? `${preview.maxSeverity}/10` : "—" },
    { label: "Hidden damage risk", value: preview.hiddenProb, text: preview.hiddenProb ? `${Math.round(preview.hiddenProb * 100)}%` : "—" },
  ];
  return (
    <div className="stat-panel">
      <div className="stat-title">{title}</div>
      {bars.map((b) => (
        <div key={b.label} className="stat-row">
          <span className="stat-label">{b.label}</span>
          <div className="stat-track">
            <div
              className={`stat-fill ${b.value >= 0.67 ? "hot" : b.value >= 0.34 ? "warm" : ""}`}
              style={{ width: `${Math.round(b.value * 100)}%` }}
            />
          </div>
          <span className="stat-value">{b.text}</span>
        </div>
      ))}
      {note && <p className="stat-note">{note}</p>}
    </div>
  );
}
